import { useEffect, useRef, useState } from 'react'
import { Check, Monitor, Moon, Sun } from 'lucide-react'
import { useThemeStore } from '../../stores/themeStore.js'

const OPTIONS = [
  { value: 'light', label: 'Clair', Icon: Sun },
  { value: 'dark', label: 'Sombre', Icon: Moon },
  { value: 'system', label: 'Automatique', hint: "Suit le réglage de l'appareil", Icon: Monitor },
]

/** Choix du thème (clair, sombre, automatique) dans la barre du haut. */
export default function ThemeSwitcher() {
  const preference = useThemeStore((state) => state.preference)
  const theme = useThemeStore((state) => state.theme)
  const setPreference = useThemeStore((state) => state.setPreference)
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setIsOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const CurrentIcon = preference === 'system' ? Monitor : theme === 'light' ? Sun : Moon

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex h-11 w-11 items-center justify-center rounded-2xl border border-ink/10 bg-ink/[0.03] text-ink/70 transition hover:border-[#5b5cf4]/25 hover:text-[#5b5cf4]"
        aria-label="Thème de l'interface"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title="Thème"
      >
        <CurrentIcon className="h-5 w-5" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 z-[70] mt-3 w-56 overflow-hidden rounded-2xl border border-ink/10 bg-panel p-1.5 shadow-[0_24px_60px_rgb(var(--rgb-shadow)/calc(0.55*var(--shadow-k)))]"
        >
          <p className="px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/40">Thème</p>
          {OPTIONS.map(({ value, label, hint, Icon }) => {
            const selected = preference === value
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setPreference(value)
                  setIsOpen(false)
                }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition ${selected ? 'bg-[#5b5cf4]/10 text-[#5b5cf4]' : 'text-ink/75 hover:bg-ink/[0.05] hover:text-ink'}`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{label}</span>
                  {hint && <span className="block text-xs text-ink/40">{hint}</span>}
                </span>
                {selected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
